import React, { useState, useEffect, useMemo } from "react";
import { message, Progress, Spin } from "antd";
import useLogout from "../useLogout";
import "./profileRoleBased.css";

const defaultDailyOTPCount = process.env.REACT_APP_API_dialyOTPs;
const defaultMonthlyOTPCount = process.env.REACT_APP_API_MonthlyOTPs;
const defaultPreviousMonthOTPCount = process.env.REACT_APP_API_PreviousMonthCount;
const bonusApi = process.env.REACT_APP_API_BonusURL;
const defaultDuplicateMonthlyCount = process.env.REACT_APP_API_DuplicateOTPs;
const configSettingAPI = process.env.REACT_APP_ConfigSetting;
const bannedAppCountApi =
  "https://gj2fi00268.execute-api.us-east-1.amazonaws.com/dev/total-banned-app-otp-count";

const Testers = ({ apiUrls }) => {
  const dailyOTPCount = apiUrls?.dailyOTPCount ?? defaultDailyOTPCount;
  const monthlyOTPCount = apiUrls?.monthlyOTPCount ?? defaultMonthlyOTPCount;
  const previousMonthOTPCount = apiUrls?.previousMonthOTPCount ?? defaultPreviousMonthOTPCount;
  const duplicateMonthlyCount = apiUrls?.duplicateMonthlyCount ?? defaultDuplicateMonthlyCount;
  const [, contextHolder] = message.useMessage();
  const [dailyCount, setDailyCount] = useState(0);
  const [monthlyCount, setMonthlyCount] = useState(0);
  const [previousMonthCount, setPreviousMonthCount] = useState(0);
  const [duplicateMonthCount, setDuplicateMonthCount] = useState(0);
  const [bonusUSD, setBonusUSD] = useState();
  const [lowerLimitPayout, setLowerLimitPayout] = useState();
  const [loading, setLoading] = useState(true);
  // const [minChargesPerMonthUSD, setMinChargesPerMonthUSD] = useState(35.99);
  const [bannedCount, setBannedCount] = useState(0);
  const [paymentRate, setPaymentRate] = useState(0.01);

  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : {};

  const logout = useLogout();

  useEffect(() => {
    fetchOTPData();
  }, []);

  const fetchOTPData = async () => {
    try {
      const userId = user.userId;
      const automationIds = [669, 1197, 1198, 1199, 1200, 1201];
      if (automationIds.includes(userId)) {
        setPaymentRate(0.005);
      }

      const [
        dailyResponse,
        bannedOtpCountResponse,
        previousMonthCountResponse,
        bonusResponse,
        monthlyResponse,
        monthlyDuplicateResponse,
        configSettingResponse,
      ] = await Promise.all([
        fetch(`${dailyOTPCount}/${userId}`),
        fetch(`${bannedAppCountApi}/${userId}`),
        fetch(`${previousMonthOTPCount}/${userId}`),
        fetch(`${bonusApi}/${userId}`),
        fetch(`${monthlyOTPCount}/${userId}`),
        fetch(`${duplicateMonthlyCount}/${userId}`),
        fetch(configSettingAPI),
      ]);

      const [
        dailyData,
        bannedOtpData,
        prevMonthData,
        bonusData,
        monthlyData,
        monthlyDuplicateData,
        configData,
      ] = await Promise.all([
        dailyResponse.json(),
        bannedOtpCountResponse.json(),
        previousMonthCountResponse.json(),
        bonusResponse.json(),
        monthlyResponse.json(),
        monthlyDuplicateResponse.json(),
        configSettingResponse.json(),
      ]);

      if (dailyData.inactive || bannedOtpData.inactive || prevMonthData.inactive ||
          monthlyData.inactive || monthlyDuplicateData.inactive) {
        logout();
        return;
      }

      setDailyCount(dailyData.otp_today);
      setBannedCount(bannedOtpData.total_count);
      setPreviousMonthCount(prevMonthData.otp_current_month);

      const bodyObject = JSON.parse(bonusData.body);
      const currentBonus = bodyObject ? bodyObject.current_month_bonus : bonusData.current_month_bonus;
      setBonusUSD(currentBonus);

      setMonthlyCount(monthlyData.otp_current_month);
      setDuplicateMonthCount(monthlyDuplicateData.duplicate_otp_current_month);

      const { lower_limit_payout } = configData[0];
      setLowerLimitPayout(lower_limit_payout || 0);
    } catch (error) {
      console.error("Error fetching OTP data:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateValidOTPsAndPayment = (monthlyCount, duplicateMonthCount) => {
    const validOTPs = monthlyCount - duplicateMonthCount;
    let payment = validOTPs * paymentRate;
    console.log("payment", payment);

    return { validOTPs, payment };
  };

  const { validOTPs, payment } = useMemo(
    () => calculateValidOTPsAndPayment(monthlyCount, duplicateMonthCount),
    [monthlyCount, duplicateMonthCount]
  );

  const progressPercent = Math.min(Math.round((validOTPs / 5000) * 100), 100);

  return (
    <div className="profile-rb">
      {contextHolder}
      <div className="profile-rb-main">
        <h1 className="profile-rb-title">Your Dashboard</h1>
        <p className="profile-rb-subtitle">
          Overview of your testing activity and earnings
        </p>

        <div className="profile-rb-alert profile-rb-alert-info">
          If your valid OTP count for the month is less than 5000, the payment
          will be sent next month.
        </div>
        <div className="profile-rb-alert profile-rb-alert-warning">
          Payment is made only to those generating a minimum of 5000 OTPs per
          month. Failure to maintain this for three consecutive months may
          result in account suspension.
        </div>

        <Spin spinning={loading} size="large">
          <div className="profile-rb-stats">
            <div className="profile-rb-stat-card">
              <div className="profile-rb-stat-label">Tests Today</div>
              <div className="profile-rb-stat-value profile-rb-stat-value-success">
                {dailyCount}
              </div>
            </div>
            <div className="profile-rb-stat-card">
              <div className="profile-rb-stat-label">Tests This Month</div>
              <div className="profile-rb-stat-value profile-rb-stat-value-success">
                {monthlyCount}
              </div>
            </div>
            <div className="profile-rb-stat-card profile-rb-stat-danger">
              <div className="profile-rb-stat-label">Duplicates This Month</div>
              <div className="profile-rb-stat-value profile-rb-stat-value-danger">
                {duplicateMonthCount}
              </div>
            </div>
            <div className="profile-rb-stat-card profile-rb-stat-success">
              <div className="profile-rb-stat-label">Valid Tests This Month</div>
              <div className="profile-rb-stat-value profile-rb-stat-value-success">
                {validOTPs}
              </div>
            </div>
            <div className="profile-rb-stat-card profile-rb-stat-warning">
              <div className="profile-rb-stat-label">Total Payment This Month</div>
              <div className="profile-rb-stat-value">
                ${(payment + (bonusUSD || 0)).toFixed(2)}
                <span className="profile-rb-stat-suffix">USD</span>
              </div>
              <div
                style={{
                  marginTop: 8,
                  fontSize: "0.75rem",
                  color: "#64748b",
                }}
              >
                Base: ${payment.toFixed(2)} · Bonus: ${(bonusUSD || 0).toFixed(2)}
              </div>
            </div>
            <div className="profile-rb-stat-card">
              <div className="profile-rb-stat-label">Previous Month</div>
              <div className="profile-rb-stat-value profile-rb-stat-value-warning">
                {previousMonthCount}
              </div>
            </div>
            <div className="profile-rb-stat-card">
              <div className="profile-rb-stat-label">Rate</div>
              <div className="profile-rb-stat-value" style={{ fontSize: "1rem" }}>
                ${paymentRate}/OTP
              </div>
            </div>
            <div className="profile-rb-stat-card">
              <div className="profile-rb-stat-label">Target Progress (5000)</div>
              <div
                className="profile-rb-stat-value"
                style={{
                  color: validOTPs >= 5000 ? "#059669" : "#dc2626",
                }}
              >
                {validOTPs}
                <span className="profile-rb-stat-suffix">/ 5000</span>
              </div>
              <div className="profile-rb-progress-wrap">
                <Progress
                  percent={progressPercent}
                  status={validOTPs >= 5000 ? "success" : "active"}
                  strokeColor={validOTPs >= 5000 ? "#10b981" : "#38bdf8"}
                  size="small"
                  showInfo={false}
                />
              </div>
            </div>
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default Testers;
